// src/composables/useDarkMode.ts
import { ref, watch, onMounted, computed, type Ref, readonly } from 'vue'

type Theme = 'light' | 'dark'

interface UseDarkModeReturn {
  isDark: Readonly<Ref<boolean>>
  theme: Ref<Theme>
  toggleTheme: () => void
  setTheme: (theme: Theme) => void
}

const THEME_STORAGE_KEY = 'theme'

export function useDarkMode(): UseDarkModeReturn {
  // Initialize from localStorage or default to 'light'
  const getInitialTheme = (): Theme => {
    if (typeof window === 'undefined') return 'light'

    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null
    if (savedTheme === 'dark' || savedTheme === 'light') {
      return savedTheme
    }

    // Optional: respect system preference on first load
    // if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
    //   return 'dark'
    // }

    return 'light'
  }

  const theme = ref<Theme>(getInitialTheme())
  const isDark = computed(() => theme.value === 'dark')

  // Apply theme to document
  const applyTheme = (newTheme: Theme): void => {
    const root = document.documentElement

    if (newTheme === 'dark') {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }

    // Also update data-theme for DaisyUI if needed
    root.setAttribute('data-theme', newTheme)

    // Save to localStorage
    localStorage.setItem(THEME_STORAGE_KEY, newTheme)
  }

  // Watch for theme changes and apply immediately
  watch(
    theme,
    (newTheme) => {
      applyTheme(newTheme)
    },
    { immediate: true }
  )

  // Toggle between light and dark
  const toggleTheme = (): void => {
    theme.value = theme.value === 'light' ? 'dark' : 'light'
  }

  // Set specific theme
  const setTheme = (newTheme: Theme): void => {
    theme.value = newTheme
  }

  // Initialize on mount (in case of SSR or hydration)
  onMounted(() => {
    applyTheme(theme.value)
  })

  return {
    isDark: readonly(isDark),
    theme,
    toggleTheme,
    setTheme,
  }
}
